<script setup lang="ts">
import { useRouteParams } from "@vueuse/router";
import { computed, type ComputedRef, ref } from "vue";
import { useRouter } from "vue-router";

import ConfirmationModal from "@/common/components/ConfirmationModal.vue";
import ContextActionMenu from "@/common/components/ContextActionMenu/ContextActionMenu.vue";
import BaseIcon from "@/common/components/icons/BaseIcon.vue";
import PageWrapper from "@/common/components/PageWrapper.vue";
import type { LinkTab } from "@/common/types";
import { useGetTrackableProductBatchByIdQuery } from "@/fabricks/batches/api/queries";
import {
  useDeleteTrackableProductMutation,
  useGetFabricksTrackableProductQuery,
} from "@/fabricks/trackableProducts/api/queries";
import { useCurrentUser } from "@/users/public/composables";

const { userHasPermission } = useCurrentUser();

definePage({
  meta: {
    auth: ["FabricksAdmin.Access"],
  },
});

const router = useRouter();
const trackableProductId = useRouteParams("trackableProductId", "", {
  transform: String,
});

const { data: trackableProduct, isPending } =
  useGetFabricksTrackableProductQuery(trackableProductId);
const { mutateAsync: deleteTrackableProduct } =
  useDeleteTrackableProductMutation();
const tabs: ComputedRef<LinkTab[]> = computed(() => [
  {
    title: "Details",
    to: `/fabricks/trackable-products/${trackableProductId.value}/details`,
  },
  {
    title: "Demo Settings",
    to: `/fabricks/trackable-products/${trackableProductId.value}/demo-settings`,
  },
  {
    title: "Chips",
    to: `/fabricks/trackable-products/${trackableProductId.value}/chips`,
  },
]);

const breadcrumbs = computed(() => [
  {
    label: "All Trackable Products",
    to: "/fabricks/trackable-products",
  },
  {
    label: "Batch",
    to: `/fabricks/batches/${trackableProduct?.value?.batchId}/details`,
  },
  {
    label: "Trackable Product Details",
    to: "#",
  },
]);
const batchId = computed(() => trackableProduct?.value?.batchId ?? "");
const batchQueryEnabled = computed(() => !!trackableProduct?.value?.batchId);
const { data: batch } = useGetTrackableProductBatchByIdQuery(batchId, {
  enabled: batchQueryEnabled,
});

const trackableProducts = computed(() => batch?.value?.trackableProducts ?? []);

const previousAndNextTrackableProduct = computed(() => {
  const trackableProductIndex = trackableProducts.value?.findIndex(
    (p) => p.id === trackableProduct.value?.id,
  );

  if (trackableProductIndex === undefined || trackableProductIndex === -1)
    return null;

  const previousTrackableProduct =
    trackableProducts.value?.[trackableProductIndex - 1];
  const nextTrackableProduct =
    trackableProducts.value?.[trackableProductIndex + 1];

  return {
    previous: previousTrackableProduct,
    next: nextTrackableProduct,
  };
});

const isDeleteModalOpen = ref(false);
const handleDeleteConfirmed = async () => {
  await deleteTrackableProduct(trackableProductId.value);
  isDeleteModalOpen.value = false;
  router.push("/fabricks/trackable-products");
};
</script>

<template>
  <PageWrapper
    :breadcrumb-links="breadcrumbs"
    :title="isPending ? 'Loading...' : trackableProduct?.name || 'Unknown'"
    :tabs="tabs"
  >
    <template #actions>
      <div
        v-if="batch"
        class="flex items-center pt-2 pb-4 gap-x-4 text-sm"
      >
        <RouterLink
          v-if="
            previousAndNextTrackableProduct &&
            previousAndNextTrackableProduct!.previous
          "
          :to="`/fabricks/trackable-products/${previousAndNextTrackableProduct.previous.id}/details`"
          class="flex items-center justify-center gap-1 text-gray-500 hover:text-gray-600"
        >
          <BaseIcon
            name="heroicons:chevron-left"
            class="size-4"
            aria-hidden="true"
          />
          <span class="truncate">
            {{ previousAndNextTrackableProduct.previous.name }}
          </span>
        </RouterLink>
        <RouterLink
          v-if="
            previousAndNextTrackableProduct &&
            previousAndNextTrackableProduct!.next
          "
          :to="`/fabricks/trackable-products/${previousAndNextTrackableProduct.next.id}/details`"
          class="flex items-center justify-center gap-1 text-gray-500 hover:text-gray-600"
        >
          <span class="truncate">
            {{ previousAndNextTrackableProduct.next.name }}
          </span>
          <BaseIcon
            name="heroicons:chevron-right"
            class="size-4"
            aria-hidden="true"
          />
        </RouterLink>
        <ContextActionMenu
          v-if="userHasPermission('FabricksTrackableProducts.Delete')"
          class="ml-4 mb-3"
          :menu-items="[
            {
              label: 'Delete',
              icon: 'heroicons:trash',
              onClick: () => {
                isDeleteModalOpen = true;
              },
            },
          ]"
        />
        <ConfirmationModal
          v-model:is-open="isDeleteModalOpen"
          warning-message="You are about to delete this trackable product. This action is irreversible. Doing this will allow you to be able to link this product to a chip again, but it will break the existing chip to be empty. So if the Chip is already shipped please contact Tech! Are you sure you want to continue?"
          @on-confirm="handleDeleteConfirmed"
        />
      </div>
    </template>
    <RouterView :trackable-product-id="trackableProductId" />
  </PageWrapper>
</template>
